import { useCallback, useEffect, useState } from "react";

export type NotificationPermissionStatus = "granted" | "denied" | "default" | "unsupported";

interface UseNotificationPermissionReturn {
  permission: NotificationPermissionStatus;
  isSupported: boolean;
  canRequestPermission: boolean;
  requestPermission: () => Promise<NotificationPermissionStatus>;
  showTestNotification: (title: string, options?: NotificationOptions) => boolean;
  getBrowserInfo: () => { name: string; canRetryDenied: boolean };
  checkPermissionStatus: () => Promise<NotificationPermissionStatus>;
}

export const useNotificationPermission = (): UseNotificationPermissionReturn => {
  const [permission, setPermission] = useState<NotificationPermissionStatus>("default");
  const [isSupported, setIsSupported] = useState(false);
  const [canRequestPermission, setCanRequestPermission] = useState(false);

  const checkPermissionStatus = useCallback(async (): Promise<NotificationPermissionStatus> => {
    try {
      if ("permissions" in navigator) {
        const permissionStatus = await navigator.permissions.query({ name: "notifications" as PermissionName });
        console.log("Permission status from navigator.permissions.query:", permissionStatus);

        const mappedPermission = permissionStatus.state as NotificationPermissionStatus;
        setPermission(mappedPermission);
        return mappedPermission;
      } else {
        const fallbackPermission = Notification.permission as NotificationPermissionStatus;
        console.log("Fallback to Notification.permission:", fallbackPermission);
        setPermission(fallbackPermission);
        return fallbackPermission;
      }
    } catch (error) {
      console.error("Error checking permission status:", error);

      const fallbackPermission = Notification.permission as NotificationPermissionStatus;
      setPermission(fallbackPermission);
      return fallbackPermission;
    }
  }, []);

  const getBrowserInfo = useCallback(() => {
    const userAgent = navigator.userAgent.toLowerCase();

    if (userAgent.includes("chrome")) {
      return { name: "chrome", canRetryDenied: false };
    } else if (userAgent.includes("firefox")) {
      return { name: "firefox", canRetryDenied: true };
    } else if (userAgent.includes("safari")) {
      return { name: "safari", canRetryDenied: false };
    } else if (userAgent.includes("edge")) {
      return { name: "edge", canRetryDenied: false };
    } else {
      return { name: "unknown", canRetryDenied: false };
    }
  }, []);

  useEffect(() => {
    const supported = "Notification" in window && typeof Notification.requestPermission === "function";
    setIsSupported(supported);

    if (supported) {
      checkPermissionStatus().then((currentPermission) => {
        console.log("Initial notification permission:", currentPermission);

        const browserInfo = getBrowserInfo();
        const canRequest =
          currentPermission === "default" || (currentPermission === "denied" && browserInfo.canRetryDenied);
        setCanRequestPermission(canRequest);
      });
    } else {
      console.log("Notifications not supported");
      setPermission("unsupported");
      setCanRequestPermission(false);
    }
  }, [getBrowserInfo, checkPermissionStatus]);

  const requestPermission = useCallback(async (): Promise<NotificationPermissionStatus> => {
    if (!isSupported) {
      return "unsupported";
    }

    try {
      const result = await Notification.requestPermission();
      const newPermission = result as NotificationPermissionStatus;
      setPermission(newPermission);

      const browserInfo = getBrowserInfo();
      const canRequest = newPermission === "default" || (newPermission === "denied" && browserInfo.canRetryDenied);
      setCanRequestPermission(canRequest);

      return newPermission;
    } catch (error) {
      console.error("Error requesting notification permission:", error);
      setPermission("denied");
      setCanRequestPermission(false);
      return "denied";
    }
  }, [isSupported, getBrowserInfo]);

  const showTestNotification = useCallback(
    (title: string, options?: NotificationOptions): boolean => {
      if (!isSupported || permission !== "granted") {
        return false;
      }

      try {
        new Notification(title, {
          icon: "/pwa-64x64.png",
          badge: "/pwa-64x64.png",
          ...options,
        });
        return true;
      } catch (error) {
        console.error("Error showing notification:", error);
        return false;
      }
    },
    [isSupported, permission],
  );

  return {
    permission,
    isSupported,
    canRequestPermission,
    requestPermission,
    showTestNotification,
    getBrowserInfo,
    checkPermissionStatus,
  };
};
